import { useMemo } from "react";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "../../../../components/UI/Avatar";
import { UsersRound } from "lucide-react";
import { useContacts } from "../../contexts/ContactsContext";
import EmptyState from "../EmptyState";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("vi-VN");
  } catch (e) {
    return "—";
  }
};

const ChipStack = ({ items = [], max = 2, getKey, getLabel, getColor }) => {
  if (!items || items.length === 0) return <span className="text-gray-300">—</span>;
  const visible = items.slice(0, max);
  const hidden = items.length - visible.length;
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((it, idx) => (
        <span
          key={getKey?.(it, idx) ?? idx}
          className="px-1.5 py-0.5 rounded text-[11px] text-white whitespace-nowrap max-w-[120px] truncate"
          style={{ backgroundColor: getColor?.(it) || "#6b7280" }}
          title={getLabel?.(it) || String(it)}
        >
          {getLabel?.(it) || String(it)}
        </span>
      ))}
      {hidden > 0 && (
        <span className="px-1.5 py-0.5 rounded text-[11px] bg-gray-100 text-gray-600">
          +{hidden}
        </span>
      )}
    </div>
  );
};

const GroupsTable = ({ items = [], loading = false }) => {
  const { lookups, shops } = useContacts();
  const shopById = useMemo(
    () => new Map(shops.map((s) => [s._id, s])),
    [shops],
  );

  if (!loading && items.length === 0) {
    return (
      <EmptyState
        icon={UsersRound}
        title="Chưa có nhóm nào"
        description="Nhóm Zalo mà tài khoản đã tham gia sẽ xuất hiện ở đây."
      />
    );
  }

  return (
    <div className="h-full overflow-auto">
      <table className="min-w-full text-[13px]">
        <thead className="bg-gray-50 sticky top-0 z-10 text-gray-600">
          <tr className="border-b border-gray-200">
            <th className="text-left px-4 py-2 font-medium">Nhóm</th>
            <th className="text-left px-4 py-2 font-medium">Số thành viên</th>
            <th className="text-left px-4 py-2 font-medium">Số admin</th>
            <th className="text-left px-4 py-2 font-medium">Admin quản lý</th>
            <th className="text-left px-4 py-2 font-medium">NV hỗ trợ</th>
            <th className="text-left px-4 py-2 font-medium">Ngày tạo</th>
            <th className="text-left px-4 py-2 font-medium">Tag cài đặt</th>
            <th className="text-left px-4 py-2 font-medium">Giai đoạn</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((c) => {
            const shop = shopById.get(c.shopId);
            const owner = shop
              ? (lookups.staffsById?.[shop.staffId] || { name: "—" })
              : { name: "—" };
            const supportStaffs = (c.staffIds || []).map(
              (id) => lookups.staffsById?.[id] || { name: id, id },
            );
            const pipeline = c.pipelineStageId
              ? lookups.pipelineStagesById?.[c.pipelineStageId]
              : null;
            const tagChips = (c.tagIds || []).map(
              (id) => lookups.tagsById?.[id] || { name: "?", id, color: "#9ca3af" },
            );
            const adminCount = Array.isArray(c.groupAdminIds)
              ? c.groupAdminIds.length
              : 0;

            return (
              <tr key={`${c.shopId}:${c.uid}`} className="hover:bg-gray-50">
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={c.avatar} alt={c.name} />
                      <AvatarFallback className="text-[11px]">
                        {c.name?.charAt(0) || "G"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div
                        className="font-medium text-gray-900 truncate max-w-[220px]"
                        title={c.name}
                      >
                        {c.name || "(Nhóm không tên)"}
                      </div>
                      {shop?.name && (
                        <div className="text-[11px] text-gray-500 truncate max-w-[220px]">
                          {shop.name}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">
                  {c.totalMember ?? "—"}
                </td>
                <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">
                  {adminCount}
                </td>
                <td className="px-4 py-2.5">
                  <span className="px-1.5 py-0.5 rounded text-[11px] bg-gray-100 text-gray-700 max-w-[140px] truncate inline-block">
                    {owner.name || "—"}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <ChipStack
                    items={supportStaffs}
                    getKey={(s, i) => s.id || s._id || i}
                    getLabel={(s) => s.name}
                    getColor={() => "#6366f1"}
                  />
                </td>
                <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                  {formatDate(c.createdAt)}
                </td>
                <td className="px-4 py-2.5">
                  <ChipStack
                    items={tagChips}
                    getKey={(t) => t.id}
                    getLabel={(t) => t.name}
                    getColor={(t) => t.color || "#9ca3af"}
                  />
                </td>
                <td className="px-4 py-2.5">
                  {pipeline ? (
                    <span
                      className="px-1.5 py-0.5 rounded text-[11px] text-white"
                      style={{ backgroundColor: pipeline.color || "#4f46e5" }}
                    >
                      {pipeline.name}
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {loading && (
        <div className="py-4 text-center text-[12px] text-gray-400">
          Đang tải…
        </div>
      )}
    </div>
  );
};

export default GroupsTable;
